<script setup lang="ts">
import { useGetAreas } from "@/areas/areasCrud";
import { useGetAreaHouses } from "@/areas/useAreaHouses";
import type { Canvass } from "@/canvasses/types";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import SaveButton from "@/components/SaveButton.vue";
import TemporalInstantInput from "@/components/TemporalInstantInput.vue";
import ToolTip from "@/components/ToolTip.vue";
import { ConflictError } from "@/gateways/apiClient";
import { Xmark } from "@iconoir/vue";
import type { MutationStatus } from "@tanstack/vue-query";
import { cloneDeep } from "lodash-es";
import { computed, ref } from "vue";
import CanvassFormAreaMap from "./CanvassFormAreaMap.vue";
import CanvassTargetsForm from "./CanvassTargetsForm.vue";
import { WysiwygEditorAsync as WysiwygEditor } from "./WysiwygEditorAsync";

const props = defineProps<{
  updatingStatus: MutationStatus;
  canvass: Canvass;
  error?: Error | null;
}>();

defineEmits<{
  update: [canvass: Canvass];
}>();

const isNameConflict = computed(() => props.error instanceof ConflictError);

const canvass = ref(cloneDeep(props.canvass));

const { areas, getAreasStatus } = useGetAreas();

const selectedArea = computed(() =>
  areas.value?.find((area) => area.id === canvass.value.area.id),
);
const { houses } = useGetAreaHouses(selectedArea);
const visibleHousesCount = computed(
  () => houses.value.filter((house) => !house.hidden).length,
);
</script>

<template>
  <form class="box column" @submit.prevent="$emit('update', canvass)">
    <div class="multi-columns">
      <div class="column">
        <div>
          <label for="name">Name</label>
          <input
            id="name"
            v-model="canvass.name"
            type="text"
            name="name"
            required
          />
          <section v-if="isNameConflict" class="error">
            Eine Haustüraktion mit diesem Namen existiert bereits.
          </section>
        </div>

        <div>
          <label for="abstract">
            Kurzbeschreibung
            <ToolTip id="abstract-tooltip">
              <p>
                Die Kurzbeschreibung ist für alle Mitglieder und neue
                Aktivst:innen auf der öffentlichen Aktionsseite sichtbar. Die
                Aktionsseite ist nur verfügbar, wenn die Aktion veröffentlicht
                wurde. Du kannst hier beschreiben, worum es in dieser Aktion
                geht, um Interessierte vorab zu informieren
              </p>
            </ToolTip>
          </label>
          <textarea v-model="canvass.abstract" rows="2" />
        </div>

        <div class="area-select">
          <HandleQueryStatus :query-status="getAreasStatus" class="column">
            <div>
              <label for="areaId">Gebiet</label>
              <select
                id="areaId"
                v-model="canvass.area.id"
                name="areaId"
                required
              >
                <option value="" disabled>Bitte wählen...</option>
                <option v-for="area in areas" :key="area.id" :value="area.id">
                  {{ area.name }}
                </option>
              </select>
            </div>

            <p>
              Übersicht der markierten Hausnummern ({{ visibleHousesCount }})
            </p>
            <div class="map">
              <CanvassFormAreaMap
                v-if="selectedArea !== undefined"
                :key="selectedArea.id"
                v-model:meeting-coordinates="canvass.meetingPoint.coordinates"
                :area="selectedArea"
                :houses="houses"
              />
              <div v-else class="cover">
                <span class="muted center">Wähle ein Gebiet aus</span>
              </div>
            </div>

            <div v-if="selectedArea !== undefined">
              <p class="muted">
                Klicke in der Karte, um einen Treffpunkt zu markieren.
              </p>
              <button
                v-if="canvass.meetingPoint.coordinates"
                class="button button--secondary"
                @click.prevent="canvass.meetingPoint.coordinates = null"
              >
                Treffpunkt entfernen
              </button>
            </div>
          </HandleQueryStatus>
        </div>

        <div>
          <label for="turf-count">
            Häuser für Teilnehmende aufteilen
            <ToolTip id="turf-count-tooltip">
              <p>
                Bitte eine Zahl zwischen 1-30 eintragen! Diese Funktion kannst
                du nutzen, um die ausgewählten Häuser für deine Teilnehmenden
                optimal aufzuteilen. Wenn beispielsweise fünf Teams an einer
                Aktion teilnehmen, kannst du "5" eintragen. Die Häuse werden
                dann gleichmäßig in fünf Gruppen eingeteilt. Jedes Team bekommt
                anschließend eine Gruppe zugeteilt.
              </p>
              <p>
                Die Zuordnung ist erst beim starten der Aktion sichtbar und ist
                jederzeit änderbar.
              </p>
            </ToolTip>
          </label>
          <input
            id="turf-count"
            v-model="canvass.turfCount"
            type="number"
            name="turf-count"
            min="1"
            max="30"
            required
          />
        </div>

        <div>
          <label for="meetingDescription">Beschreibung des Treffpunkts</label>
          <input
            id="meetingDescription"
            v-model="canvass.meetingPoint.description"
            type="text"
            name="meetingDescription"
          />
        </div>

        <div>
          <label for="meetingTime">Zeitpunkt zum Treffen</label>
          <TemporalInstantInput
            id="meetingTime"
            v-model="canvass.meetingPoint.time"
            name="meetingTime"
          />
        </div>
      </div>
      <div class="column">
        <div>
          <label for="conversationGuide">Gesprächsleitfaden</label>
          <WysiwygEditor
            :model-value="canvass.conversationGuide ?? ''"
            @update:model-value="(value) => (canvass.conversationGuide = value)"
          />
        </div>

        <CanvassTargetsForm
          v-model:canvass-targets="canvass.canvassTargetsWithProgress"
        />
      </div>
    </div>

    <div class="row row-end">
      <RouterLink
        :to="{ name: 'canvasses' }"
        class="button button--secondary"
        role="button"
        ><Xmark /> Zurück zur Übersicht</RouterLink
      >
      <SaveButton :saving-status="updatingStatus">Speichern</SaveButton>
    </div>
  </form>
</template>

<style scoped>
.map {
  block-size: 20rem;
}
</style>
